import React, {useEffect, useMemo} from 'react'
import {
  DateTime,
  useBrowserFocus,
  usePrevious,
  useRPCContext,
  useWatchGeolocation,
} from '@therms/react-modules'
import dayjs from 'dayjs'
import {Button, ButtonWithActionModal, Icon} from '@therms/atalaya'
import {getDevice} from '@therms/web-js'
import {useUpdateEffect} from 'ahooks'
import {distanceBetweenLatLng} from '@src/utils/google-maps/distance-between-latlng'
import {MdDangerous, MdGpsFixed} from 'react-icons/md'
import {BiSync} from 'react-icons/bi'

// interface StatusGeoTrackingProps {}

function StatusGeoTracking() {
  const {rpcService} = useRPCContext()
  const focused = useBrowserFocus()

  const geo = useWatchGeolocation()

  const previousGeo = usePrevious(geo)
  const previousFocused = usePrevious(focused)

  useEffect(() => {
    const time = `[${dayjs().format('HH:mm:ss a')}]`

    let msg: string

    if (previousGeo) {
      const distFromLast =
        previousGeo.lat && geo.lat
          ? distanceBetweenLatLng(
              previousGeo.lat,
              previousGeo.lng,
              geo.lat,
              geo.lng,
            ).toFixed(3)
          : '0.0'

      msg = `${time} accuracy: ${
        geo.accuracy
      }, distance: ${distFromLast}, time: ${dayjs(geo.timestamp).format(
        'HH:mm:ss a',
      )}, loading: ${geo.loading}`

      if (geo.error) msg += `, error: ${geo.error?.message}`
    } else {
      msg = `${time} Starting geolocation watcher`
    }
  }, [geo?.error, geo?.loading, geo?.timestamp])

  useUpdateEffect(() => {
    if (!previousFocused && focused && !geo.loading) {
      if (
        previousGeo?.timestamp &&
        dayjs(previousGeo?.timestamp).isAfter(dayjs().subtract(20, 'seconds'))
      ) {
        console.info(
          'skipping geolocation refresh, previous geo timestamp was less than 20sec ago',
        )
        return
      }

      geo.refresh()
    }
  }, [focused])

  const showOutdateLocation = useMemo(() => {
    if (!geo.timestamp) return false

    // if geo coords older than 5 min and geo is not loading
    return (
      dayjs(geo.timestamp).isBefore(dayjs().subtract(5, 'minutes')) &&
      !geo.loading
    )
  }, [geo.timestamp, focused])

  useEffect(() => {
    // refresh every 2.5min
    const i = setInterval(
      () => {
        geo.refresh()
      },
      60 * 2.5 * 1000,
    )

    return () => clearInterval(i)
  }, [geo.timestamp])

  useEffect(() => {
    if (previousGeo?.lat !== geo.lat || previousGeo?.lng !== geo.lng) {
      if (geo?.accuracy && geo.accuracy > 3200) {
        console.info(
          `Skipping sending device geo because the accuracy is too low: ${geo.accuracy}`,
        )

        return
      }

      if (geo.accuracy && geo.lat && geo.lng && geo.timestamp) {
        rpcService.Users.SaveUserDeviceGeo.v1.call({
          deviceGeo: {
            accuracy: geo.accuracy,
            coords: {
              lat: geo.lat,
              lng: geo.lng,
            },
            device: getDevice(),
            timestamp: geo.timestamp,
          },
        })
      }
    }
  }, [previousGeo])

  let accuracyColorVariant = ''
  let accuracyLabel = ''

  if (geo?.accuracy) {
    if (geo.accuracy >= 3200) {
      accuracyColorVariant = 'critical'
      accuracyLabel = 'No GPS'
    } else if (geo.accuracy >= 500 && geo.accuracy < 1600) {
      accuracyColorVariant = 'caution'
      accuracyLabel = 'Poor GPS'
    } else if (geo.accuracy >= 1600) {
      accuracyColorVariant = 'critical'
      accuracyLabel = 'Poor GPS'
    } else {
      accuracyLabel = `${geo.accuracy}m`
    }
  }

  return (
    <div className="flex flex-col justify-center text-sm">
      {!geo.error && (
        <div className="text-color-subtle flex w-full justify-between opacity-80">
          <span className="truncate">
            {geo.loading ? (
              <Icon className="animate-spin opacity-25" Icon={BiSync} />
            ) : (
              <Icon Icon={MdGpsFixed} size="sm" color={accuracyColorVariant} />
            )}{' '}
            <span className={`text-color-${accuracyColorVariant}`}>
              Location tracking
            </span>
          </span>

          <span className="truncate">
            {accuracyLabel && (
              <span className={`text-color-${accuracyColorVariant} opacity-50`}>
                {accuracyLabel}
              </span>
            )}
          </span>
        </div>
      )}

      {showOutdateLocation && (
        <Button
          className="bg-caution-faded text-color-caution"
          onClick={geo.refresh}
        >
          Last location update
          <span className="ml-xs">
            <DateTime
              mode="time"
              relative
              timestamp={dayjs(geo.timestamp).toISOString()}
            />
          </span>
        </Button>
      )}

      {geo?.error?.TIMEOUT && (
        <div className="text-color-critical">
          {geo.loading && (
            <Icon
              className="animate-spin opacity-25"
              color="critical"
              Icon={BiSync}
            />
          )}
          Location service timed-out
        </div>
      )}

      {(geo?.error?.PERMISSION_DENIED || geo?.error?.POSITION_UNAVAILABLE) && (
        <ButtonWithActionModal
          actionText={
            <>
              <Icon Icon={MdDangerous} /> Fix Location Permissions
            </>
          }
          body={
            <div>
              <div>
                Your browser does is not allowing THERMS location permissions.
                Ensure that you have granted your browser location permissions.
              </div>

              <div className="mt-base">
                <a
                  href="https://support.google.com/chrome/answer/142065?hl=en&co=GENIE.Platform%3DDesktop"
                  target="_blank"
                  rel="noreferrer"
                >
                  <Button subtle variant="info">
                    Chrome Browser permissions guide
                  </Button>
                </a>
              </div>
            </div>
          }
          onAction={() => undefined}
          title="Browser Location Permissions Issue"
          variant="critical"
        />
      )}
    </div>
  )
}

export {StatusGeoTracking}
